import { deriveTheme, type ColorTokens } from "@cos/theme";
import type { ThemeInput } from "@cos/shared";

function kebabCase(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function tokensToDeclarations(tokens: ColorTokens): string {
  return Object.entries(tokens)
    .map(([key, value]) => `--cos-${kebabCase(key)}: ${value};`)
    .join("\n");
}

/**
 * One derivation, one CSS surface: `deriveTheme` runs identically here, in
 * the panel's live preview, and at save-time contrast validation (PLAN.md
 * "Theming"). `surface: 'auto'` ships both palettes behind
 * `prefers-color-scheme` rather than picking one; `light`/`dark` force it.
 * Values land as custom properties on `:host` — every structural rule in
 * `styles.ts` reads through `var(--cos-*)`, never a literal color.
 */
export function themeToCss(input: ThemeInput): string {
  const derived = deriveTheme(input);
  const shared = `
    --cos-radius: ${derived.radius};
    --cos-radius-launcher: ${derived.radiusLauncher};
    --cos-spacing-scale: ${derived.spacingScale};
    --cos-font-family: ${derived.fontFamily};
  `;

  if (input.surface === "dark") {
    return `:host { ${shared} ${tokensToDeclarations(derived.dark)} color-scheme: dark; }`;
  }

  if (input.surface === "light") {
    return `:host { ${shared} ${tokensToDeclarations(derived.light)} color-scheme: light; }`;
  }

  return `
    :host { ${shared} ${tokensToDeclarations(derived.light)} color-scheme: light; }
    @media (prefers-color-scheme: dark) {
      :host { ${tokensToDeclarations(derived.dark)} color-scheme: dark; }
    }
  `;
}
